import React, { useState } from "react";
import { queryCache, useQuery } from "react-query";
import {
  EuiBadge,
  EuiButton,
  EuiCallOut,
  EuiEmptyPrompt,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLoadingSpinner,
  EuiPanel,
  EuiSpacer,
  EuiTabbedContent,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { getRelease, getReleases } from "../../api/appReleases";
import ActivityTab from "./activityTab";
import HoldsTab from "./holdsTab";
import OutletPicker from "./outletPicker";
import OutletsTable from "./outletsTable";
import ReleaseSummary from "./releaseSummary";
import {
  getErrorMessage,
  getStatusDisplay,
  isManualMode,
  RELEASE_QUERY_KEY,
  REFRESH_INTERVAL_MS,
} from "./helpers";

const ErrorCallOut = ({ error }) => (
  <EuiCallOut
    size="s"
    color="danger"
    iconType="alert"
    title={getErrorMessage(error)}
  />
);

const AppReleases = () => {
  const [selectedId, setSelectedId] = useState(null);

  const {
    data: list,
    isLoading,
    isFetching,
    error: listError,
  } = useQuery(
    [RELEASE_QUERY_KEY, "list"],
    getReleases,
    { refetchInterval: REFRESH_INTERVAL_MS, retry: false },
  );

  const releases = list ? list.releases : [];
  const releaseId =
    selectedId ||
    (list && list.active_release_id) ||
    (releases[0] && releases[0].id);

  const { data: detail, error: detailError } = useQuery(
    [RELEASE_QUERY_KEY, "detail", releaseId],
    () => getRelease(releaseId),
    {
      enabled: Boolean(releaseId),
      refetchInterval: REFRESH_INTERVAL_MS,
      retry: false,
    },
  );

  // The detail response has no id, so use the one that was requested.
  const release = detail ? { ...detail, id: releaseId } : null;
  // The badge shows how this environment picks outlets, not how one release went out.
  const mode = (list && list.mode) || (release && release.mode);
  const waitingVersion = list && list.waiting_upload;
  const activeRelease =
    list && releases.find((item) => item.id === list.active_release_id);

  const tabs = release
    ? [
        ...(release.mode === "random"
          ? [
              {
                id: "outlets",
                name: "Outlets",
                content: (
                  <>
                    <EuiSpacer size="m" />
                    <OutletsTable key={release.id} release={release} />
                  </>
                ),
              },
            ]
          : []),
        {
          id: "hold",
          name: "Hold",
          content: (
            <>
              <EuiSpacer size="m" />
              <HoldsTab />
            </>
          ),
        },
        {
          id: "activity",
          name: "Activity",
          content: (
            <>
              <EuiSpacer size="m" />
              <ActivityTab releaseId={release.id} />
            </>
          ),
        },
      ]
    : [];

  return (
    <>
      <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiTitle size="m">
            <h1>App Releases</h1>
          </EuiTitle>
        </EuiFlexItem>
        {mode ? (
          <EuiFlexItem grow={false}>
            <EuiBadge color="hollow">
              {mode === "random"
                ? "Random outlets"
                : isManualMode(mode)
                ? "Hand-picked outlets"
                : "Every outlet at once"}
            </EuiBadge>
          </EuiFlexItem>
        ) : null}
        <EuiFlexItem />
        <EuiFlexItem grow={false}>
          {/* Opening or refreshing the list makes the backend read the feed files,
              so a new upload shows up as a Draft. */}
          <EuiButton
            size="s"
            iconType="refresh"
            isLoading={isFetching && !isLoading}
            onClick={() => queryCache.invalidateQueries(RELEASE_QUERY_KEY)}
          >
            Check now
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size="m" />

      {isLoading ? <EuiLoadingSpinner size="l" /> : null}
      {listError ? <ErrorCallOut error={listError} /> : null}

      {waitingVersion ? (
        <>
          <EuiCallOut
            size="s"
            color="warning"
            iconType="clock"
            title={`${waitingVersion} is on the test board. Finish or roll back ${
              activeRelease ? activeRelease.version : "the active release"
            } first.`}
          />
          <EuiSpacer size="m" />
        </>
      ) : null}

      {list && releases.length === 0 ? (
        <EuiEmptyPrompt
          title={<h2>No releases yet</h2>}
          body={<p>A release appears here after the upload script runs.</p>}
        />
      ) : null}

      {releases.length > 0 ? (
        <EuiFlexGroup gutterSize="s" responsive={false} wrap>
          {releases.map((item) => (
            <EuiFlexItem grow={false} key={item.id}>
              <EuiPanel
                paddingSize="s"
                color={item.id === releaseId ? "primary" : "plain"}
                hasBorder
                onClick={() => setSelectedId(item.id)}
              >
                <EuiText size="s">
                  <strong>{item.version}</strong>
                </EuiText>
                <EuiBadge color={getStatusDisplay(item.status).color}>
                  {getStatusDisplay(item.status).label}
                </EuiBadge>
              </EuiPanel>
            </EuiFlexItem>
          ))}
        </EuiFlexGroup>
      ) : null}

      {detailError ? (
        <>
          <EuiSpacer size="m" />
          <ErrorCallOut error={detailError} />
        </>
      ) : null}

      {release ? (
        <>
          <EuiSpacer size="m" />
          <EuiPanel hasBorder>
            <ReleaseSummary release={release} />
          </EuiPanel>
          {isManualMode(release.mode) ? (
            <>
              <EuiSpacer size="m" />
              <EuiPanel hasBorder>
                <OutletPicker key={release.id} release={release} />
              </EuiPanel>
            </>
          ) : null}
          <EuiSpacer size="m" />
          <EuiPanel hasBorder>
            <EuiTabbedContent key={release.mode} tabs={tabs} />
          </EuiPanel>
        </>
      ) : null}
    </>
  );
};

export default AppReleases;
